import React, { useState, useEffect } from "react";
import { useBoolean } from "@fluentui/react-hooks";
import i18n from "i18next";

// Fluent UI Imports
import { fetchWrapper } from "../../helpers/fetchwrapper";
import ProductDetails from "../ProductDetails/ProductDetails";

import { baseUrl } from "../../helpers/constants";
import AddUserProductDialog from "./AddUserProductDialog";

import { helperMethods } from "../../helpers/helperMethods";
import { useNavigate } from "react-router-dom";
import {
  getProductByUserProductGuid,
  hasRightsForUrl,
} from "./userProductsService";
import { StackShim } from "@fluentui/react-migration-v8-v9";
import { Button, MessageBar, ProgressBar } from "@fluentui/react-components";
import {
  IosArrowRtl24Regular,
  WrenchScrewdriverRegular,
} from "@fluentui/react-icons";
import { IProduktDto } from "../../Models/IProdukt";
import UserProductsCommandBar from "./UserProductsCommandBar";
import { IUserProductsListDto } from "../../Models/IUserProductsListDto";
import UserProductsList from "./UserProductsList";
import { IUserProduct } from "../../Models/IUserProduct";
import Header from "../Layout/Header";
import useWindowSize from "../../helpers/useWindowSize";
import HeaderTextAnimation from "../Layout/HeaderTextAnimation";
import { IAppUserDto } from "../../Models/IAppUserDto";
import authService from "../api-authorization/AuthorizeService";

interface IUserProductsProps {
  location?: string;
}

function UserProducts(props: IUserProductsProps) {
  const isDe = i18n.language.includes("de");
  const windowSize = useWindowSize();
  const [loading, setLoading] = useState<boolean>(false);
  const [isEditModeOn, setIsEditModeOn] = useState<boolean>(false);
  const [currentUser, setCurrentUser] = useState<IAppUserDto | undefined>(
    undefined,
  );

  const getCurrentUser = () => {
    authService.getUser().then((user) => {
      if (user) {
        fetchWrapper
          .getWithAuth(`${baseUrl}/api/account/getcurrentuser`)
          .then((result: IAppUserDto) => {
            setCurrentUser(result);
          })
          .catch((error) => {});
      }
    });
  };

  useEffect(() => {
    getCurrentUser();
  }, []);

  const navigate = useNavigate();

  const isToolbox = window.location.pathname
    .toLowerCase()
    .includes("/mytoolbox");
  const isPartnerLounge = window.location.pathname
    .toLowerCase()
    .includes("/partnerlounge");

  const [textFilter, setTextFilter] = useState<string>("");
  const [product, setProduct] = useState<IProduktDto | undefined>();
  const [userProduct, setUserProduct] = useState<IUserProduct | undefined>(
    undefined,
  );
  const [allUserProducts, setAllUserProducts] = useState<
    IUserProductsListDto | undefined
  >(undefined);
  const [filteredUserProducts, setFilteredUserProducts] = useState<
    IUserProductsListDto | undefined
  >(undefined);
  const [foundByToProductList, setFoundByToProductList] = useState<
    { userProductGuid: string; foundBy: string[] }[]
  >([]);
  const [isAddUserProductOpen, setIsAddUserProductOpen] =
    useState<boolean>(false);
  const [backendMessage, setBackendMessage] = useState<
    { message: string; type: "error" | "success" } | undefined
  >(undefined);

  useEffect(() => {
    if (window.location.pathname.toLowerCase().includes("/partnerlounge")) {
      helperMethods.addAccessedModuleCountValue("PartnerLounge");
    } else if (window.location.pathname.toLowerCase().includes("/mytoolbox")) {
      helperMethods.addAccessedModuleCountValue("Toolbox");
    }
  }, []);

  useEffect(() => {
    if (!hasRightsForUrl(isPartnerLounge, isToolbox)) {
      navigate("/");
    }
  }, []);

  useEffect(() => {
    if (!isAddUserProductOpen) {
      setTimeout(() => {
        getUserProducts();
      }, 200);
    }
  }, [window.location.pathname, isAddUserProductOpen]);

  useEffect(() => {
    if (window.location.search.includes("?addproduct&")) {
      setIsAddUserProductOpen(true);
    }
  }, [window.location.pathname]);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location?.search);
    const _guid = searchParams?.get("guid");

    if (_guid && !product && allUserProducts) {
      let _userProduct: IUserProduct | undefined;

      // Zuerst in der eigenen Liste suchen
      _userProduct = allUserProducts?.ownProductslist.find(
        (x) => x.userProduktGuid == _guid,
      );

      if (_userProduct == undefined) {
        _userProduct = allUserProducts?.sharedProductslist.find(
          (x) => x.userProduktGuid == _guid,
        );
      }

      if (_userProduct) {
        getProductByUserProductGuid(_guid).then((product) => {
          setProduct(product);
          setUserProduct(_userProduct);
        });
      }
    } else if (!_guid) {
      setProduct(undefined);
    }
  }, [window.location, window.location?.search, allUserProducts]);

  useEffect(() => {
    if (userProduct && userProduct.userProduktGuid) {
      const _userProduct = allUserProducts?.ownProductslist.find(
        (x) => x.userProduktGuid == userProduct.userProduktGuid,
      );

      if (_userProduct) {
        setUserProduct(_userProduct);
      }
    }
  }, [allUserProducts]);

  useEffect(() => {
    applyFilter();
  }, [textFilter, allUserProducts]);

  const getUserProducts = () => {
    if (!loading) {
      setLoading(true);
      fetchWrapper
        .getWithAuth(
          `${baseUrl}/api/UserProduct/GetMyProducts?isPartnerLounge=${isPartnerLounge}&isToolbox=${isToolbox}`,
        )
        .then((result: any) => {
          setLoading(false);
          setAllUserProducts(result);
        })
        .finally(() => {});
    }
  };

  const applyFilter = () => {
    if (
      !loading &&
      allUserProducts != undefined &&
      textFilter != "" &&
      isDe != undefined
    ) {
      setLoading(true);
      fetchWrapper
        .getWithAuth(
          `${baseUrl}/api/UserProduct/ApplyUserProductsFilter?textFilter=${textFilter}&isDe=${isDe}`,
        )
        .then(
          (result: {
            ownProductsList: { item1: IUserProduct; item2: string[] }[];
            sharedProductsList: { item1: IUserProduct; item2: string[] }[];
          }) => {
            if (!textFilter) {
              setFilteredUserProducts(undefined);
              return;
            }

            var foundByList: { userProductGuid: string; foundBy: string[] }[] =
              [
                ...result.ownProductsList.map((x) => {
                  return {
                    userProductGuid: x.item1.userProduktGuid,
                    foundBy: x.item2,
                  };
                }),
                ...result.sharedProductsList.map((x) => {
                  return {
                    userProductGuid: x.item1.userProduktGuid,
                    foundBy: x.item2,
                  };
                }),
              ];

            setFoundByToProductList(foundByList);

            setFilteredUserProducts({
              ownProductslist: result.ownProductsList.map((x) => {
                return x.item1;
              }),
              sharedProductslist: result.sharedProductsList.map((x) => {
                return x.item1;
              }),
              statusMessage: "",
            });
          },
        )
        .finally(() => {
          setLoading(false);
        });
    } else if (textFilter == "") {
      setFilteredUserProducts(undefined);
      setFoundByToProductList([]);
    }
    /* 
    if (!allUserProducts) {
      return;
    }

    let newOwnUserProducts = filterUserProducts(
      allUserProducts.ownProductslist
    );

    let newSharedUserProducts = filterUserProducts(
      allUserProducts.sharedProductslist
    );

    setFilteredUserProducts({
      ownProductslist: newOwnUserProducts,
      sharedProductslist: newSharedUserProducts,
      statusMessage: allUserProducts?.statusMessage ?? "",
    }); */
  };

  useEffect(() => {
    setTimeout(() => {
      setBackendMessage(undefined);
    }, 2500);
  }, [backendMessage]);

  return (
    <div>
      {allUserProducts && isAddUserProductOpen && (
        <AddUserProductDialog
          userProducts={allUserProducts.ownProductslist}
          location={window.location}
          isPartnerLounge={isPartnerLounge}
          isToolbox={isToolbox}
          isDialogOpen={isAddUserProductOpen}
          onDismiss={() => {
            setIsAddUserProductOpen(false);
          }}
          onProductAdded={() => {
            // Wenn der LINK SystemID etc entält navigieren
            if (window.location.search.includes("?addproduct&")) {
              navigate(window.location.pathname);
            }
            setIsAddUserProductOpen(false);
          }}
        />
      )}

      <Header
        HTMLElement={
          <StackShim horizontal verticalAlign="center">
            {(windowSize.width > 1310 ||
              windowSize.width < 1024 ||
              !product) && (
              <Button
                icon={
                  <WrenchScrewdriverRegular fontSize={24} color="#ff6600" />
                }
                size="large"
                appearance="subtle"
                onClick={() => {
                  navigate(isToolbox ? "/mytoolbox" : "/partnerlounge");
                  setProduct(undefined);
                  setUserProduct(undefined);
                }}
              >
                {isToolbox
                  ? "MY TOOLBOX"
                  : isPartnerLounge
                    ? "PARTNER LOUNGE"
                    : "ERROR"}
              </Button>
            )}
            {windowSize.width < 1311 && windowSize.width > 1024 && product && (
              <Button
                style={{ paddingLeft: "0px" }}
                size="small"
                onClick={() => {
                  navigate(isToolbox ? "/mytoolbox" : "/partnerlounge");
                  setProduct(undefined);
                  setUserProduct(undefined);
                }}
                appearance="transparent"
              >
                <WrenchScrewdriverRegular fontSize={24} color="#ff6600" />
              </Button>
            )}
            {product && (
              <div style={{ display: "flex", alignItems: "center" }}>
                <IosArrowRtl24Regular color="#ff6600" />
                <Button size="large" appearance="subtle">
                  <HeaderTextAnimation
                    text={`${product.produktId} - ${
                      i18n.language.includes("de")
                        ? product.artikel?.benennungDE
                        : product.artikel?.benennungEN
                    }`}
                  />
                </Button>
              </div>
            )}
          </StackShim>
        }
      />
      {backendMessage && (
        <MessageBar intent={backendMessage.type}>
          {backendMessage.message}
        </MessageBar>
      )}

      {product ? (
        <ProductDetails
          userProduct={userProduct}
          salesView={false}
          product={product}
          isPanelOpen={product != undefined}
          onDismiss={() => {
            setProduct(undefined);
            setUserProduct(undefined);

            getUserProducts();
          }}
          onRefresh={() => {
            getUserProducts();
          }}
        />
      ) : (
        <>
          {currentUser && (
            <UserProductsCommandBar
              currentUser={currentUser}
              currentTextFilter={textFilter}
              editModeOn={isEditModeOn}
              showMakeProductsAvailable={
                allUserProducts?.ownProductslist.find(
                  (x) => x.hasFreigaben == false,
                )
                  ? true
                  : false
              }
              onEditModeChanged={setIsEditModeOn}
              onTextFilterChanged={setTextFilter}
              onAddProductClicked={() => {
                setIsAddUserProductOpen(true);
              }}
              onBackendmessage={setBackendMessage}
              onRefresh={getUserProducts}
            />
          )}
          {allUserProducts && (
            <UserProductsList
              onPageChanged={() => {
                // Workaround wegen Display Fehler mit Input Feld
                if (isEditModeOn) {
                  setIsEditModeOn(false);
                  setTimeout(() => {
                    setIsEditModeOn(true);
                  }, 50);
                }
              }}
              isPartnerLounge={isPartnerLounge}
              isEditModeOn={isEditModeOn}
              userProductsListDto={
                filteredUserProducts != undefined
                  ? filteredUserProducts
                  : allUserProducts
              }
              textFilter={textFilter}
              onRefresh={getUserProducts}
              foundByToProductList={foundByToProductList}
            />
          )}
        </>
      )}

      {loading && <ProgressBar />}
    </div>
  );
}

export default UserProducts;
